import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useLocation } from 'react-router-dom'
import { LoginForm } from '../components/auth/LoginForm'
import { useAuth } from '../hooks/useAuth'
import { consumeAuthReturnTo } from '../utils/authRedirect'

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuth()

  useEffect(() => {
    // If already logged in, honour any pending return-URL first so a
    // stale /login tab that regains auth (e.g. login from another
    // tab) lands the user where they left off instead of on Home.
    if (!isLoading && isAuthenticated) {
      const stateFrom = (location.state as { from?: string } | null)?.from
      const target = stateFrom || consumeAuthReturnTo() || '/'
      navigate(target, { replace: true })
    }
  }, [navigate, isAuthenticated, isLoading, location.state])

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Login - AgenX</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </div>
      </>
    )
  }

  // Don't show login form if already authenticated (will redirect)
  if (isAuthenticated) {
    return null
  }

  return (
    <>
      <Helmet>
        <title>Login - AgenX</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full space-y-8 p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div>
            {/* New AgenX wordmark — black for light surfaces, white for
                dark. Both sources are pre-cropped SVGs so they scale
                cleanly to any height. */}
            <img
              className="mx-auto h-10 w-auto block dark:hidden"
              src="/main_logo/agenx_logo_black.svg"
              alt="AgenX"
            />
            <img
              className="mx-auto h-10 w-auto hidden dark:block"
              src="/main_logo/agenx_logo_white.svg"
              alt="AgenX"
              aria-hidden="true"
            />
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              Sign in to your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Enter your credentials to continue
            </p>
          </div>
          <LoginForm />
        </div>
      </div>
    </>
  )
}

